/**
 * Промпти для відгуків: наші три + до п'яти своїх (owner 2026-09-12).
 *
 * Один компонент на два місця: шторка «Згенерувати відповідь» (там є кнопка
 * генерації) і розділ у профілі «Генерація відповідей» (там її немає, лише
 * налаштування). Власник: «я б у профілі додав розділ генерацію відповідей,
 * щоб можна було все налаштувати й не тільки з кнопки».
 *
 * Розкриття — через `Collapse`, тобто плавно; кнопки дій темніші за фон, під
 * промптом є підказка, що його можна листати.
 */
import { useState } from "react";

import { Collapse } from "@/components/Collapse";
import { Icon } from "@/components/Icon";
import { showAppAlert } from "@/lib/appAlert";
import { tg } from "@/lib/telegram";
import {
  useAIPresets,
  useDeleteAIPreset,
  useSaveAIPreset,
  type AIPreset,
} from "@/api/hooks";

const NAME_MIN = 2;
const PROMPT_MIN = 100;  // owner 2026-09-12
const PROMPT_MAX = 4000;

export function AIPresetsManager({
  onGenerate,
  initialForm = false,
  onFormOpenChange,
}: {
  /** Якщо передано — у рядку зʼявляється кнопка «Згенерувати». */
  onGenerate?: (presetId: number) => void;
  initialForm?: boolean;
  /** Повідомляємо назовні, що відкрита форма: шторка ховає свій хрестик,
   *  бо у формі вже є «Скасувати» (owner 2026-09-12). */
  onFormOpenChange?: (open: boolean) => void;
}) {
  const presetsQ = useAIPresets();
  const saveMut = useSaveAIPreset();
  const delMut = useDeleteAIPreset();

  const [openPrompt, setOpenPrompt] = useState<number | null>(null);
  const [showForm, setShowFormRaw] = useState(initialForm);
  const setShowForm = (v: boolean) => {
    setShowFormRaw(v);
    onFormOpenChange?.(v);
  };
  const [editId, setEditId] = useState<number | null>(null);
  const [name, setName] = useState("");
  const [prompt, setPrompt] = useState("");

  const items: AIPreset[] = presetsQ.data?.items ?? [];
  const ownLeft = presetsQ.data?.own_left ?? 0;
  const ownLimit = presetsQ.data?.own_limit ?? 5;

  const startCreate = () => {
    if (ownLeft <= 0) {
      showAppAlert(
        `Своїх промптів можна мати ${ownLimit}. Видали один, щоб додати новий.`,
        { title: "Ліміт промптів" },
      );
      return;
    }
    setEditId(null);
    setName("");
    setPrompt("");
    setShowForm(true);
  };

  const startEdit = (item: AIPreset) => {
    setEditId(item.id);
    setName(item.name);
    setPrompt(item.prompt);
    setShowForm(true);
  };

  const save = () => {
    const n = name.trim();
    const pr = prompt.trim();
    if (n.length < NAME_MIN) {
      showAppAlert(`Назва — мінімум ${NAME_MIN} символи (до 40).`, {
        title: "Назва промпта",
      });
      return;
    }
    if (pr.length < PROMPT_MIN) {
      showAppAlert(
        `Опиши стиль детальніше — мінімум ${PROMPT_MIN} символів. Зараз: ${pr.length}.`,
        { title: "Замало" },
      );
      return;
    }
    saveMut.mutate(
      { id: editId ?? undefined, name: n.slice(0, 40), prompt: pr.slice(0, PROMPT_MAX) },
      {
        onSuccess: () => {
          tg()?.HapticFeedback?.notificationOccurred?.("success");
          setShowForm(false);
          setEditId(null);
          setName("");
          setPrompt("");
        },
        onError: (e) =>
          showAppAlert((e as Error).message, { title: "Помилка збереження" }),
      },
    );
  };

  const confirmDelete = (item: AIPreset) => {
    tg()?.HapticFeedback?.impactOccurred?.("light");
    showAppAlert(`Видалити «${item.name}»? Цю дію не скасувати.`, {
      title: "Видалити промпт?",
      emoji: "😔",
      buttons: [
        {
          label: "Так, видалити",
          variant: "danger",
          onClick: () =>
            delMut.mutate(item.id, {
              onSuccess: () =>
                tg()?.HapticFeedback?.notificationOccurred?.("warning"),
              onError: (e) =>
                showAppAlert((e as Error).message, { title: "Помилка" }),
            }),
        },
        { label: "Скасувати", variant: "ghost" },
      ],
    });
  };

  if (showForm) {
    return (
      <div>
        <p className="mb-3 text-[0.8125rem] leading-relaxed text-ink-dim [text-wrap:balance]">
          {editId
            ? "Промпт. Відгук генерується строго за ним — правь як хочеш."
            : "Свій промпт: дай назву й напиши інструкцію. Відгук генерується строго за нею."}
        </p>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value.slice(0, 40))}
          maxLength={40}
          placeholder="Назва промпта"
          className="w-full rounded-xl border border-ink/[0.06] bg-bg/60 px-3 py-2.5 text-[0.875rem] text-ink placeholder:text-ink-faint focus:outline-none focus:ring-2 focus:ring-primary/30"
        />
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value.slice(0, PROMPT_MAX))}
          maxLength={PROMPT_MAX}
          rows={editId ? 12 : 8}
          placeholder="Промпт: тон, структура, довжина, чого уникати, чим закінчувати…"
          className="mt-2 w-full resize-none rounded-xl border border-ink/[0.06] bg-bg/60 px-3 py-2.5 text-[0.875rem] leading-relaxed text-ink placeholder:text-ink-faint focus:outline-none focus:ring-2 focus:ring-primary/30"
        />
        <div
          className={
            "mt-1 text-[0.6875rem] " +
            (prompt.length > 0 && prompt.length < PROMPT_MIN
              ? "text-danger"
              : "text-ink-dim")
          }
        >
          {prompt.length}/{PROMPT_MAX} · мінімум {PROMPT_MIN} · відгук
          генерується строго за цим промптом
        </div>
        <div className="mt-3 flex gap-2">
          <button
            type="button"
            onClick={() => {
              setShowForm(false);
              setEditId(null);
            }}
            className="flex-1 rounded-full py-2.5 font-display text-[0.8125rem] font-semibold text-ink-dim active:bg-ink/5"
          >
            Скасувати
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saveMut.isPending}
            className="flex-1 rounded-full bg-primary py-2.5 font-display text-[0.8125rem] font-bold text-white active:bg-primary-deep disabled:opacity-50"
          >
            {saveMut.isPending
              ? "Зберігаю…"
              : editId
                ? "Зберегти зміни"
                : "Зберегти промпт"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      {presetsQ.isLoading ? (
        <div className="py-6 text-center text-[0.8125rem] text-ink-dim">
          Завантажую промпти…
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((item) => (
            <div key={item.id} className="rounded-xl border border-ink/[0.06] bg-bg/60">
              <div className="flex items-center gap-2 px-4 py-3">
                <button
                  type="button"
                  onClick={() =>
                    setOpenPrompt(openPrompt === item.id ? null : item.id)
                  }
                  className="flex min-w-0 flex-1 items-center gap-2 text-left"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="truncate font-display text-[0.875rem] font-semibold text-ink">
                        {item.name}
                      </span>
                      {/* Стрілка стоїть ПОРУЧ ІЗ НАЗВОЮ — так одразу видно, що
                          розкривається саме промпт (owner 2026-09-12). */}
                      <span
                        className="shrink-0"
                        style={{
                          transform:
                            openPrompt === item.id ? "rotate(180deg)" : "none",
                          transition: "transform var(--dur) var(--ease-ios)",
                        }}
                      >
                        <Icon name="chevron" size={13} color="#A8A29C" />
                      </span>
                    </div>
                    <div className="text-[0.75rem] text-ink-dim">
                      {item.is_own ? "твій промпт" : "наш промпт"}
                    </div>
                  </div>
                </button>
                {onGenerate && (
                  <button
                    type="button"
                    onClick={() => onGenerate(item.id)}
                    aria-label="Згенерувати"
                    title="Згенерувати"
                    className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-primary text-white active:scale-90"
                  >
                    <Icon name="brain" size={15} color="#FFFFFF" />
                  </button>
                )}
              </div>
              <Collapse open={openPrompt === item.id}>
                <div className="border-t border-ink/[0.06] px-4 py-3">
                  <div className="max-h-40 overflow-y-auto whitespace-pre-wrap text-[0.75rem] leading-relaxed text-ink-dim [overflow-wrap:anywhere]">
                    {item.prompt}
                  </div>
                  <div className="mt-2 text-center font-mono text-[0.625rem] uppercase tracking-[0.08em] text-primary">
                    промпт можна листати
                  </div>
                  <div className="mt-2.5 flex gap-2">
                    <button
                      type="button"
                      onClick={() => startEdit(item)}
                      className="flex-1 rounded-full bg-[#E9DFCE] py-2 font-display text-[0.75rem] font-semibold text-ink active:scale-[0.98]"
                    >
                      Редагувати
                    </button>
                    <button
                      type="button"
                      onClick={() => confirmDelete(item)}
                      className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-[#E9DFCE] text-primary active:scale-90"
                      aria-label="Видалити"
                    >
                      <Icon name="trash" size={13} />
                    </button>
                  </div>
                </div>
              </Collapse>
            </div>
          ))}

          <button
            type="button"
            onClick={startCreate}
            className="flex w-full items-center justify-center gap-2.5 rounded-xl border-2 border-dashed border-primary/35 px-5 py-4 font-display text-[0.875rem] font-semibold text-primary active:bg-surface-soft"
          >
            <Icon name="plus" size={16} color="#FE6B04" />
            Додати свій промпт
          </button>
          <div className="text-center text-[0.6875rem] text-ink-faint">
            Своїх промптів: {ownLimit - ownLeft} з {ownLimit}
          </div>
        </div>
      )}
    </div>
  );
}
